import React, { useRef, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getCartoLightAllTileUrl, CARTO_BASEMAP_ATTRIBUTION } from '../shared/cartoBasemap';

// Simple patch for Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Get marker color based on location type
const getMarkerColor = (type) => {
  switch (type?.toLowerCase()) {
    case 'accommodation':
      return 'blue';
    case 'attraction':
      return 'red';
    case 'transportation':
      return 'green';
    case 'restaurant':
      return 'orange';
    case 'destination':
      return 'violet';
    case 'day location':
      return 'gold';
    default:
      return 'gray';
  }
};

// Create custom icon with optional badge for day maps
const createIconWithTime = (type, isHighlighted = false, badgeText = null, badgeKind = 'time') => {
  try {
    const color = isHighlighted ? 'yellow' : getMarkerColor(type);
    const iconSize = isHighlighted ? [30, 50] : [25, 41];
    
    if (badgeText !== null && badgeText !== undefined && badgeText !== '') {
      const isTime = badgeKind === 'time';
      const isName = badgeKind === 'name';
      const isNumber = badgeKind === 'number';
      const safeText = String(badgeText)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      const badgeBorderRadius = isTime || isName ? '4px' : '50%';
      const badgeMinWidth = isNumber ? '20px' : '20px';
      const badgeMaxWidth = isName ? '220px' : 'none';
      const badgePadding = isNumber ? '0' : isName ? '2px 8px' : '2px 6px';
      const fontSize = isName ? '9px' : isTime ? '10px' : '11px';
      const displayName = isName && safeText.length > 28 ? `${safeText.slice(0, 26)}…` : safeText;
      
      return new L.DivIcon({
        className: 'custom-marker-with-time',
        html: `
          <div style="position: relative; display: inline-block;">
            <img 
              src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png" 
              style="width: 25px; height: 41px;"
              alt="marker"
            />
            <span 
              style="
                position: absolute;
                top: -8px;
                left: 50%;
                transform: translateX(-50%);
                background-color: #fff;
                color: #000;
                border: 2px solid ${color === 'red' ? '#c33' : color === 'blue' ? '#3388ff' : color === 'green' ? '#2ecc71' : color === 'orange' ? '#ff9800' : color === 'violet' ? '#9b59b6' : color === 'gold' ? '#f1c40f' : '#666'};
                border-radius: ${badgeBorderRadius};
                width: auto;
                min-width: ${badgeMinWidth};
                max-width: ${badgeMaxWidth};
                height: ${isNumber ? '20px' : '20px'};
                padding: ${badgePadding};
                display: ${isName ? 'inline-block' : 'flex'};
                align-items: center;
                justify-content: center;
                font-size: ${fontSize};
                font-weight: bold;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                z-index: 1000;
                white-space: nowrap;
                text-align: center;
                line-height: 20px;
                overflow: hidden;
                text-overflow: ellipsis;
              "
            >${displayName}</span>
          </div>
        `,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34]
      });
    }
    
    // Default icon without badge
    return new L.Icon({
      iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: iconSize,
      iconAnchor: isHighlighted ? [15, 50] : [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });
  } catch (error) {
    console.error("Error creating icon:", error);
    return new L.Icon.Default();
  }
};

// Day map controller that fits bounds to markers and saves/restores the view state for a specific day
function DayMapController({ locations, planId, dayIndex, markerPositions, disableSavedView = false, onMapReady }) {
  const map = useMap();
  
  // Load saved view state from localStorage when the map is initialized
  useEffect(() => {
    if (!map) return;
    
    const timer = setTimeout(() => {
      try {
        map.invalidateSize();
        
        // If we have a planId and dayIndex, try to load saved view state
        if (!disableSavedView && planId && dayIndex !== null && dayIndex !== undefined) {
          const storageKey = `map_view_${planId}_${dayIndex + 1}`;
          const savedView = localStorage.getItem(storageKey);
          if (savedView) {
            const { center, zoom } = JSON.parse(savedView);
            map.setView(center, zoom);
            console.log('Restored map view for plan:', planId, `day ${dayIndex + 1}`);
            onMapReady?.();
            return; // Don't fit bounds if we've restored a saved view
          }
        }
        const bounds = new L.LatLngBounds();
        let hasValidLocations = false;
        
        for (let i = 0; i < locations.length; i++) {
          const location = locations[i];
          if (location.gps?.latitude && location.gps?.longitude) {
            // Use offset position if available, otherwise use original
            if (markerPositions && markerPositions[i]) {
              bounds.extend([markerPositions[i].lat, markerPositions[i].lng]);
            } else {
              bounds.extend([location.gps.latitude, location.gps.longitude]);
            }
            hasValidLocations = true;
          }
        }
        
        if (hasValidLocations && bounds.isValid()) {
          map.fitBounds(bounds, { padding: [50, 50] });
        }

        // Leaflet needs a second invalidate after fitBounds when inside modals or flex layouts
        setTimeout(() => {
          map.invalidateSize();
          onMapReady?.();
        }, disableSavedView ? 350 : 150);
      } catch (error) {
        console.error("Error updating map view:", error);
      }
    }, disableSavedView ? 250 : 100);
    
    return () => clearTimeout(timer);
  }, [locations, map, planId, dayIndex, markerPositions, disableSavedView, onMapReady]);
  
  // Save the current view state to localStorage when the map view changes
  useEffect(() => {
    if (disableSavedView || !map || !planId || dayIndex === null || dayIndex === undefined) return;
    
    const storageKey = `map_view_${planId}_${dayIndex + 1}`;
    
    // Flag to track if the map has been interacted with by the user
    let userInteracted = false;
    
    const handleMapMove = () => {
      try {
        // Only save if user has actually interacted with the map
        if (!userInteracted) return;
        
        const center = map.getCenter();
        const zoom = map.getZoom();
        const viewState = {
          center: [center.lat, center.lng],
          zoom
        };
        localStorage.setItem(storageKey, JSON.stringify(viewState));
        console.log('Saved map view for plan:', planId, `day ${dayIndex + 1}`);
      } catch (error) {
        console.error("Error saving map view:", error);
      }
    };
    
    // Track user interaction with the map
    const handleUserInteraction = () => {
      userInteracted = true;
    };
    
    map.on('moveend', handleMapMove);
    map.on('zoomend', handleMapMove);
    
    // Add listeners for user interaction events
    map.on('dragstart', handleUserInteraction);
    map.on('zoomstart', handleUserInteraction);
    map.on('click', handleUserInteraction);
    
    return () => {
      map.off('moveend', handleMapMove);
      map.off('zoomend', handleMapMove);
      map.off('dragstart', handleUserInteraction);
      map.off('zoomstart', handleUserInteraction);
      map.off('click', handleUserInteraction);
    };
  }, [map, planId, dayIndex]);
  
  return null;
}

/**
 * DayMap component specifically for displaying activities for a single day
 * Shows time badges instead of day numbers
 */
function DayMap({
  locations = [],
  height = 400,
  className = '',
  onMarkerClick,
  planId,
  dayIndex,
  selectedActivity,
  disableSavedView = false,
  onMapReady,
  interactive = true,
  language,
  markerBadgeMode = 'hours',
}) {
  const { t } = useTranslation(undefined, language ? { lng: language } : undefined);
  const mapRef = useRef(null);
  const [mapKey] = useState(Date.now());
  
  // Function to translate activity type
  const translateActivityType = (type) => {
    if (!type) return '';
    const typeLower = type.toLowerCase();
    switch (typeLower) {
      case 'accommodation':
        return t('planDetail.days.accommodation');
      case 'attraction':
        return t('planDetail.days.attraction');
      case 'transportation':
        return t('planDetail.days.transportation');
      case 'restaurant':
        return t('planDetail.days.restaurant') || 'Restaurant';
      case 'destination':
        return t('planDetail.days.destination') || 'Destination';
      case 'day location':
        return t('planDetail.days.dayLocation') || 'Day Location';
      default:
        return type;
    }
  };
  
  // Process locations to ensure they have valid coordinates
  const validLocations = locations.filter(location => {
    return location.gps && location.gps.latitude && location.gps.longitude;
  });

  // Function to calculate offset positions for overlapping markers
  // Groups markers by location and applies circular offset pattern
  const calculateMarkerPositions = (locations) => {
    const COORDINATE_THRESHOLD = 0.0001; // ~11 meters - adjust based on zoom level
    const OFFSET_RADIUS = 0.0003; // ~33 meters - distance to offset markers
    
    // Group locations by similar coordinates
    const locationGroups = [];
    const processedIndices = new Set();
    
    locations.forEach((location, index) => {
      if (processedIndices.has(index)) return;
      
      const group = [index];
      const baseLat = location.gps.latitude;
      const baseLng = location.gps.longitude;
      
      // Find all locations at the same position
      locations.forEach((otherLocation, otherIndex) => {
        if (otherIndex !== index && !processedIndices.has(otherIndex)) {
          const latDiff = Math.abs(otherLocation.gps.latitude - baseLat);
          const lngDiff = Math.abs(otherLocation.gps.longitude - baseLng);
          
          if (latDiff < COORDINATE_THRESHOLD && lngDiff < COORDINATE_THRESHOLD) {
            group.push(otherIndex);
            processedIndices.add(otherIndex);
          }
        }
      });
      
      processedIndices.add(index);
      locationGroups.push({ indices: group, baseLat, baseLng });
    });
    
    // Calculate offset positions for each group
    const positions = locations.map((location, index) => {
      // Find which group this location belongs to
      const group = locationGroups.find(g => g.indices.includes(index));
      
      if (!group || group.indices.length === 1) {
        // No offset needed for single markers
        return {
          lat: location.gps.latitude,
          lng: location.gps.longitude
        };
      }
      
      // Calculate circular offset
      const positionInGroup = group.indices.indexOf(index);
      const totalInGroup = group.indices.length;
      
      // Distribute markers in a circle
      const angle = (2 * Math.PI * positionInGroup) / totalInGroup;
      const offsetLat = OFFSET_RADIUS * Math.cos(angle);
      const offsetLng = OFFSET_RADIUS * Math.sin(angle);
      
      return {
        lat: group.baseLat + offsetLat,
        lng: group.baseLng + offsetLng
      };
    });
    
    return positions;
  };

  // Calculate offset positions for overlapping markers
  const markerPositions = calculateMarkerPositions(validLocations);

  const getMarkerBadge = (location, index) => {
    switch (markerBadgeMode) {
      case 'hours':
        return location.time
          ? { text: location.time, kind: 'time' }
          : null;
      case 'numbers':
        if (location.markerNumber != null) {
          return { text: String(location.markerNumber), kind: 'number' };
        }
        return null;
      case 'names':
        return location.name
          ? { text: location.name, kind: 'name' }
          : null;
      case 'none':
      default:
        return null;
    }
  };

  // Default center if no locations
  const defaultCenter = [40.7128, -74.0060]; // New York

  return (
    <div style={{ height, width: '100%' }} className={className}>
      <MapContainer 
        key={mapKey}
        center={defaultCenter}
        zoom={5} 
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
        dragging={interactive}
        touchZoom={interactive}
        scrollWheelZoom={interactive}
        doubleClickZoom={interactive}
        boxZoom={interactive}
        keyboard={interactive}
        zoomControl={interactive}
      >
        <TileLayer
          attribution={CARTO_BASEMAP_ATTRIBUTION}
          url={getCartoLightAllTileUrl()}
        />
        
        <DayMapController
          locations={validLocations}
          planId={planId}
          dayIndex={dayIndex}
          markerPositions={markerPositions}
          disableSavedView={disableSavedView}
          onMapReady={onMapReady}
        />
        
        {validLocations.map((location, index) => {
          // Check if this location matches the selected activity
          const isHighlighted = selectedActivity && location.name === selectedActivity.name;
          const badge = getMarkerBadge(location, index);
          
          // Use offset position if available, otherwise use original position
          const position = markerPositions[index] 
            ? [markerPositions[index].lat, markerPositions[index].lng]
            : [location.gps.latitude, location.gps.longitude];
          
          return (
            <Marker 
              key={index} 
              position={position}
              icon={createIconWithTime(
                location.type,
                isHighlighted,
                badge?.text ?? null,
                badge?.kind ?? 'time'
              )}
              eventHandlers={{
                click: (e) => {
                  if (onMarkerClick) onMarkerClick(location);
                }
              }}
            >
              <Popup>
                {location.name}
                {isHighlighted && <strong> ({t('planDetail.locations.selected')})</strong>}
                <br />
                {location.address}
                {location.time && (
                  <>
                    <br />
                    {t('planDetail.days.dayMap.time')}: {location.time}
                  </>
                )}
                {location.type && (
                  <>
                    <br />
                    {translateActivityType(location.type)}
                  </>
                )}
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

export default DayMap;
